import React from 'react'
import readingTime from 'reading-time'
import { toPlainText } from 'src/utils/utils'

const siteUrl = 'https://asktia.com'
const authorSiteUrl = siteUrl
const organizationId = `${siteUrl}/#organization`

export interface EditorialAuthor {
  id?: string
  _id?: string
  content: {
    main: {
      title: string
      slug: {
        current: string
      }
      pageFolderSlug?: string
      position?: string
      image: {
        asset: {
          _id: string
        }
      }
    }
  }
}

export interface SchemaProps {
  pagePath: string
  title: string
  publishedAt: any
  updatedAt: any
  description?: string
  keywords?: string | string[]
  articleType?: {
    title?: string
  }
  image: {
    asset: {
      url: string
      metadata?: {
        dimensions?: {
          width?: number
          height?: number
        }
      }
    }
  }
  editorialText?: any[]
  authors?: Array<EditorialAuthor | null>
}

const getCitation = (blocks: any[]) => {
  const name = toPlainText(blocks).trim()

  if (!name) return null

  const url = blocks
    .flatMap(block => block?.markDefs || [])
    .map(definition => {
      if (definition?._type === 'externalLink') return definition.url
      if (definition?._type === 'link') return definition.href
      return undefined
    })
    .find(Boolean)
    ?.trim()

  if (!url) return name

  return {
    '@type': 'CreativeWork',
    name,
    url,
  }
}

const getCitationKey = (citation: string | Record<string, string>) =>
  typeof citation === 'string' ? citation : `${citation.name}|${citation.url}`

export const generateSchema = ({
  pagePath,
  title,
  publishedAt,
  updatedAt,
  description,
  keywords,
  articleType,
  image,
  editorialText,
  authors,
}: SchemaProps) => {
  const fullUrl = new URL(pagePath, siteUrl).toString()
  const articleId = `${fullUrl}#article`
  const referenceHeadingIndex =
    editorialText?.findIndex(block => {
      if (block?._type !== 'block') return false

      return /^references:?$/i.test(toPlainText([block]).trim())
    }) ?? -1
  const articleBlocks =
    referenceHeadingIndex >= 0
      ? editorialText?.slice(0, referenceHeadingIndex)
      : editorialText
  const articleText = articleBlocks?.length
    ? toPlainText(articleBlocks).trim()
    : ''
  const fallbackDescription =
    articleText.length > 160
      ? `${articleText.slice(0, 157).trimEnd()}...`
      : articleText
  const schemaDescription = description?.trim() || fallbackDescription
  const referenceSectionCitations =
    referenceHeadingIndex >= 0
      ? editorialText
          ?.slice(referenceHeadingIndex + 1)
          .filter(block => block?._type === 'block')
          .map(block => getCitation([block]))
          .filter(Boolean) || []
      : []
  const footnoteCitations =
    editorialText?.flatMap(block =>
      (block?.markDefs || [])
        .filter(definition => definition?._type === 'footnote')
        .map(definition => getCitation(definition?.text || []))
        .filter(Boolean)
    ) || []
  const citations = [...referenceSectionCitations, ...footnoteCitations].filter(
    (citation, index, allCitations) =>
      allCitations.findIndex(
        candidate => getCitationKey(candidate) === getCitationKey(citation)
      ) === index
  )
  const schemaKeywords = (Array.isArray(keywords)
    ? keywords
    : keywords?.split(/[,;]/) || []
  )
    .map(keyword => keyword.trim())
    .filter(Boolean)
  const organization = {
    '@type': 'MedicalOrganization',
    '@id': organizationId,
    name: 'Tia',
    url: `${siteUrl}/`,
  }
  const articleAuthors = authors
    ?.filter((author): author is EditorialAuthor =>
      Boolean(author?.content?.main?.title && author.content.main.slug?.current)
    )
    ?.map(author => {
      const main = author.content.main
      const slug = main.slug.current

      const isTia =
        main.title.trim().toLowerCase() === 'tia' ||
        slug.trim().toLowerCase() === 'tia'

      if (isTia) return organization

      const pathParts = ['team', main.pageFolderSlug, slug].filter(Boolean)
      const url = new URL(`/${pathParts.join('/')}/`, authorSiteUrl).toString()

      return {
        '@type': 'Person',
        '@id': `${authorSiteUrl}/#/schema/person/${slug}`,
        name: main.title,
        url,
      }
    })
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': articleId,
    author: articleAuthors?.length ? articleAuthors : organization,
    headline: title,
    description: schemaDescription || undefined,
    datePublished: publishedAt,
    dateModified: updatedAt || publishedAt,
    url: fullUrl,
    image: {
      '@type': 'ImageObject',
      url: image.asset.url,
      width: image.asset.metadata?.dimensions?.width,
      height: image.asset.metadata?.dimensions?.height,
    },
    wordCount: articleText ? readingTime(articleText).words : undefined,
    articleSection: articleType?.title || undefined,
    keywords: schemaKeywords.length ? schemaKeywords : undefined,
    inLanguage: 'en-US',
    publisher: {
      ...organization,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/tia-logo.png`,
        width: 154,
        height: 94,
      },
    },
    citation: citations.length ? citations : undefined,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl,
    },
  }

  return schema
}
export interface MedicalSchemaProps {
  pagePath: string
  title: string
  slug?: {
    current?: string
  }
  description?: string
  image?: {
    asset: {
      url?: string
      _id?: string
    }
  }
  schemaKind?: 'medicalCategory' | 'appointment'
}

const organization = {
  '@type': 'Organization',
  name: "Tia Women's Health",
  url: siteUrl,
}

const medicalSpecialtiesBySlug: Record<string, string> = {
  gynecology: 'Gynecologic',
  'primary-care': 'PrimaryCare',
}

export const generateMedicalSchema = ({
  pagePath,
  title,
  slug,
  description,
  image,
  schemaKind = 'medicalCategory',
}: MedicalSchemaProps) => {
  const fullUrl = new URL(pagePath, siteUrl).toString()
  const imageUrl = image?.asset?.url
  const specialty = medicalSpecialtiesBySlug[slug?.current || '']

  const serviceEntity: any = {
    '@type': 'Service',
    '@id': `${fullUrl.replace(/\/$/, '')}/#service`,
    name: title,
    serviceType: title,
    provider: organization,
  }

  if (description) serviceEntity.description = description
  if (imageUrl) serviceEntity.image = imageUrl

  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${fullUrl.replace(/\/$/, '')}/#webpage`,
    url: fullUrl,
    name: title,
    mainEntity: serviceEntity,
  }

  if (description) schema.description = description
  if (imageUrl) {
    schema.primaryImageOfPage = {
      '@type': 'ImageObject',
      url: imageUrl,
    }
  }

  if (schemaKind === 'appointment') {
    schema.about = {
      '@id': serviceEntity['@id'],
    }
  } else if (specialty) {
    const specialtyUrl = `https://schema.org/${specialty}`
    const specialtyEntity = {
      '@id': specialtyUrl,
      '@type': 'MedicalSpecialty',
      name: title,
      sameAs: specialtyUrl,
      ...(description && { description }),
    }

    schema.about = specialtyEntity
    schema.mainEntity = {
      ...serviceEntity,
      category: specialtyEntity,
    }
  } else {
    schema.about = serviceEntity
  }

  return schema
}

interface AuthorCredential {
  credentialCategory: 'license' | 'certificate'
  name: string
  recognizedBy?: {
    name?: string
    url?: string
  }
}

export interface AuthorSchemaDetails {
  npi?: string
  credentials?: AuthorCredential[]
  occupation?: string
  sameAs?: string[]
}

export interface AuthorProfileSchemaProps {
  pagePath: string
  title: string
  slug: {
    current: string
  }
  position?: string
  text?: any[]
  specialties?: string[]
  image: {
    asset: {
      url: string
      metadata?: {
        dimensions?: {
          width?: number
          height?: number
        }
      }
    }
  }
  authorSchema?: AuthorSchemaDetails
}

export const generateAuthorProfileSchema = ({
  pagePath,
  title,
  slug,
  position,
  text,
  specialties,
  image,
  authorSchema = {},
}: AuthorProfileSchemaProps) => {
  const fullUrl = new URL(pagePath, authorSiteUrl).toString()
  const imageUrl = image.asset.url
  const dimensions = image.asset.metadata?.dimensions
  const description = text?.length ? toPlainText(text).trim() : ''
  const credentials = authorSchema.credentials
    ?.filter(credential => credential?.credentialCategory && credential?.name)
    .map(credential => ({
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: credential.credentialCategory,
      name: credential.name,
      ...(credential.recognizedBy?.name && {
        recognizedBy: {
          '@type': 'GovernmentOrganization',
          name: credential.recognizedBy.name,
          ...(credential.recognizedBy.url && {
            url: credential.recognizedBy.url,
          }),
        },
      }),
    }))
  const knowsAbout = specialties?.filter(Boolean)
  const sameAs = authorSchema.sameAs?.filter(Boolean)

  const person: any = {
    '@type': 'Person',
    '@id': `${authorSiteUrl}/#/schema/person/${slug.current}`,
    name: title,
    url: fullUrl,
    worksFor: {
      '@type': 'MedicalOrganization',
      '@id': `${authorSiteUrl}/#organization`,
      name: 'Tia',
      url: `${authorSiteUrl}/`,
    },
  }

  if (position) person.jobTitle = position
  if (description) person.description = description
  person.image = {
    '@type': 'ImageObject',
    url: imageUrl,
    ...(dimensions?.width && { width: dimensions.width }),
    ...(dimensions?.height && { height: dimensions.height }),
    caption: title,
  }
  if (authorSchema.npi) {
    person.identifier = {
      '@type': 'PropertyValue',
      propertyID: 'NPI',
      name: 'National Provider Identifier',
      value: authorSchema.npi,
    }
  }
  if (credentials?.length) person.hasCredential = credentials
  if (authorSchema.occupation) {
    person.hasOccupation = {
      '@type': 'Occupation',
      name: authorSchema.occupation,
    }
  }
  if (knowsAbout?.length) person.knowsAbout = knowsAbout
  if (sameAs?.length) person.sameAs = sameAs

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': fullUrl,
    url: fullUrl,
    name: `${title} | Tia`,
    mainEntity: person,
  }
}
